import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Save, Trash2, Sparkles, ArrowUp, ArrowDown, X } from 'lucide-react';
import api from '../../services/api';
import { field, ghost, primary, muted, card, rowBorder, errorText } from '../time/styles';

// Lifecycle setup → Checklists: onboarding and exit checklists. Each task has
// an owner, a due date relative to the joining date / last working day, and
// a type (tick, form, upload, sign, clearance).

const TYPE_LABELS = { FULL_TIME: 'Full-time', CONTRACTOR: 'Contractor', PARTNER: 'Partner', INTERN: 'Intern' };
const KIND_LABELS = { ONBOARDING: 'Onboarding', OFFBOARDING: 'Exit' };
const newTask = () => ({ title: '', instructions: '', type: 'TICK', ownerType: 'HR', offsetDays: 0, required: true });

const whenText = (n, kind) => {
    const anchor = kind === 'ONBOARDING' ? 'joining' : 'the last day';
    if (n === 0) return kind === 'ONBOARDING' ? 'on the joining day' : 'on the last day';
    return `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} ${n < 0 ? 'before' : 'after'} ${anchor}`;
};

const TaskRow = ({ task, n, count, meta, users, kind, onChange, onMove, onRemove }) => {
    const set = (k, v) => onChange({ ...task, [k]: v });
    const label = (text) => `${text}, task ${n + 1}`;
    return (
        <li style={{ borderTop: rowBorder, padding: '12px 0', display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <span style={{ ...muted, minWidth: 18 }}>{n + 1}.</span>
                <input aria-label={label('Task title')} placeholder="What needs doing" maxLength={150} style={{ ...field, flex: '1 1 220px' }} value={task.title} onChange={e => set('title', e.target.value)} />
                <select aria-label={label('Task type')} style={field} value={task.type} onChange={e => set('type', e.target.value)}>
                    {meta.taskTypes.map(t => <option key={t} value={t}>{meta.taskTypeLabels[t]}</option>)}
                </select>
                <select aria-label={label('Done by')} style={field} value={task.type === 'SIGN' ? 'EMPLOYEE' : task.ownerType} disabled={task.type === 'SIGN'} onChange={e => set('ownerType', e.target.value)}>
                    {meta.ownerTypes.map(t => <option key={t} value={t}>{meta.ownerLabels[t]}</option>)}
                </select>
                {task.ownerType === 'USER' && task.type !== 'SIGN' && (
                    <select aria-label={label('Person')} style={field} value={task.ownerUserId || ''} onChange={e => set('ownerUserId', e.target.value)}>
                        <option value="">Choose…</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                )}
                <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" style={ghost} disabled={n === 0} onClick={() => onMove(-1)} aria-label={label('Move up')}><ArrowUp size={14} /></button>
                    <button type="button" style={ghost} disabled={n === count - 1} onClick={() => onMove(1)} aria-label={label('Move down')}><ArrowDown size={14} /></button>
                    <button type="button" style={ghost} onClick={onRemove} aria-label={label('Remove')}><X size={14} /></button>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', paddingLeft: 26 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={muted}>Due</span>
                    <input type="number" aria-label={label('Due, days from the date')} min={-90} max={180} style={{ ...field, width: 80 }} value={task.offsetDays} onChange={e => set('offsetDays', e.target.value === '' ? '' : Math.trunc(Number(e.target.value)))} />
                    <span style={muted}>{Number.isInteger(task.offsetDays) ? whenText(task.offsetDays, kind) : ''}</span>
                </label>
                {task.type === 'FORM' && (
                    <select aria-label={label('Form')} style={field} value={task.formId || ''} onChange={e => set('formId', e.target.value)}>
                        <option value="">Choose the form…</option>
                        {meta.forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                )}
                {task.type === 'UPLOAD' && (
                    <select aria-label={label('Document kind')} style={field} value={task.uploadKind || ''} onChange={e => set('uploadKind', e.target.value)}>
                        <option value="">Choose the document…</option>
                        {meta.uploadKinds.map(k => <option key={k} value={k}>{meta.uploadKindLabels[k]}</option>)}
                    </select>
                )}
                {task.type === 'SIGN' && (
                    <select aria-label={label('Letter')} style={field} value={task.letterTemplateId || ''} onChange={e => set('letterTemplateId', e.target.value)}>
                        <option value="">Choose the letter…</option>
                        {meta.letters.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                )}
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="checkbox" checked={task.required} onChange={e => set('required', e.target.checked)} /><span>Required</span>
                </label>
            </div>
            <textarea aria-label={label('Instructions')} placeholder="Instructions (optional)" maxLength={2000} style={{ ...field, marginLeft: 26, minHeight: 40 }} value={task.instructions || ''} onChange={e => set('instructions', e.target.value)} />
            {task.type === 'SIGN' && meta.letters.length === 0 && <p style={{ ...muted, margin: '0 0 0 26px' }}>No letters ask for a signature yet. Tick "The employee signs this letter" on a template in the Letters tab.</p>}
            {task.type === 'FORM' && meta.forms.length === 0 && <p style={{ ...muted, margin: '0 0 0 26px' }}>No forms yet. Create one in the Forms tab.</p>}
        </li>
    );
};

const Editor = ({ initial, meta, users, departments, say, onDone }) => {
    const [t, setT] = useState(initial);
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setT(prev => ({ ...prev, [k]: v }));
    const setTask = (i, v) => set('tasks', t.tasks.map((x, j) => (j === i ? v : x)));
    const move = (i, d) => { const a = [...t.tasks]; [a[i], a[i + d]] = [a[i + d], a[i]]; set('tasks', a); };
    const toggle = (k, v) => {
        const w = t.applyWhen || { departmentIds: [], employmentTypes: [] };
        const list = w[k] || [];
        set('applyWhen', { ...w, [k]: list.includes(v) ? list.filter(x => x !== v) : [...list, v] });
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = { kind: t.kind, name: t.name, description: t.description, applyWhen: t.applyWhen, isActive: t.isActive, tasks: t.tasks.map(x => ({ ...x, ownerType: x.type === 'SIGN' ? 'EMPLOYEE' : x.ownerType, offsetDays: Number.isInteger(x.offsetDays) ? x.offsetDays : 0 })) };
            const r = t.id ? await api.put(`/lifecycle/checklists/${t.id}`, body) : await api.post('/lifecycle/checklists', body);
            setT(r.data);
            say(true, `Saved "${r.data.name}"`);
        } catch (e) {
            say(false, errorText(e, 'Could not save the checklist'));
        } finally {
            setSaving(false);
        }
    };
    const remove = async () => {
        if (!window.confirm(`Delete the checklist "${t.name}"? Checklists already started keep their tasks.`)) return;
        try {
            await api.delete(`/lifecycle/checklists/${t.id}`);
            say(true, `Deleted "${t.name}"`);
            onDone();
        } catch (e) {
            say(false, errorText(e, 'Could not delete the checklist'));
        }
    };

    const w = t.applyWhen || { departmentIds: [], employmentTypes: [] };
    return (
        <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'grid', gap: 4, flex: '1 1 220px' }}><span style={muted}>Checklist name</span>
                    <input style={field} maxLength={100} value={t.name} onChange={e => set('name', e.target.value)} />
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>For</span>
                    <select style={field} value={t.kind} onChange={e => set('kind', e.target.value)} disabled={!!t.id}>
                        <option value="ONBOARDING">Onboarding</option>
                        <option value="OFFBOARDING">Exit</option>
                    </select>
                </label>
            </div>
            <fieldset style={{ border: rowBorder, borderRadius: 6, padding: 12 }}>
                <legend style={muted}>Suggest it automatically for (leave empty for everyone)</legend>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {Object.entries(TYPE_LABELS).map(([k, l]) => (
                        <label key={k} style={{ display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={(w.employmentTypes || []).includes(k)} onChange={() => toggle('employmentTypes', k)} />{l}</label>
                    ))}
                </div>
                {departments.length > 0 && (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                        {departments.map(d => (
                            <label key={d.id} style={{ display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={(w.departmentIds || []).includes(d.id)} onChange={() => toggle('departmentIds', d.id)} />{d.name}</label>
                        ))}
                    </div>
                )}
            </fieldset>
            <div>
                <strong style={{ color: 'var(--text-main)' }}>Tasks</strong>
                <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {t.tasks.map((task, i) => (
                        <TaskRow key={task.key || i} task={task} n={i} count={t.tasks.length} meta={meta} users={users} kind={t.kind}
                            onChange={v => setTask(i, v)} onMove={d => move(i, d)} onRemove={() => set('tasks', t.tasks.filter((_, j) => j !== i))} />
                    ))}
                </ol>
                <button type="button" style={{ ...ghost, marginTop: 8 }} onClick={() => set('tasks', [...t.tasks, newTask()])}><Plus size={14} /> Add task</button>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={t.isActive !== false} onChange={e => set('isActive', e.target.checked)} /><span>Available when starting a checklist</span>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={primary} onClick={save} disabled={saving}><Save size={14} /> {saving ? 'Saving…' : 'Save checklist'}</button>
                {t.id && <button style={ghost} onClick={remove}><Trash2 size={14} /> Delete</button>}
            </div>
        </div>
    );
};

const ChecklistsTab = ({ users, departments, say }) => {
    const [meta, setMeta] = useState(null);
    const [editing, setEditing] = useState(null);
    const load = useCallback(() => api.get('/lifecycle/checklists').then(r => setMeta(r.data)).catch(e => say(false, errorText(e, 'Could not load checklists'))), [say]);
    useEffect(() => { load(); }, [load]);

    const addStarters = async () => {
        try {
            const r = await api.post('/lifecycle/checklists/starters');
            say(true, r.data.added.length ? `Added ${r.data.added.join(' and ')}` : 'The starter checklists are already there');
            load();
        } catch (e) {
            say(false, errorText(e, 'Could not add the starter checklists'));
        }
    };

    if (!meta) return <p style={muted}>Loading…</p>;
    if (editing) {
        return (
            <section className="card" style={card}>
                <button style={{ ...ghost, marginBottom: 16 }} onClick={() => { setEditing(null); load(); }}>← All checklists</button>
                <Editor key={editing.id || 'new'} initial={editing} meta={meta} users={users} departments={departments} say={say} onDone={() => { setEditing(null); load(); }} />
            </section>
        );
    }
    return (
        <section className="card" style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                <p style={{ ...muted, margin: 0, fontSize: 14 }}>What has to happen when someone joins or leaves, who does it, and by when.</p>
                <div style={{ display: 'flex', gap: 8 }}>
                    {meta.startersAvailable && <button style={ghost} onClick={addStarters}><Sparkles size={14} /> Add starter checklists</button>}
                    <button className="btn btn-primary" style={primary} onClick={() => setEditing({ kind: 'ONBOARDING', name: '', description: '', applyWhen: { departmentIds: [], employmentTypes: [] }, tasks: [newTask()], isActive: true })}><Plus size={14} /> New checklist</button>
                </div>
            </div>
            {meta.templates.length === 0 ? <p style={muted}>No checklists yet. The starter set gives you a standard onboarding and exit checklist to edit.</p> : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {meta.templates.map(t => (
                        <li key={t.id} style={{ borderTop: rowBorder, padding: '10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, opacity: t.isActive ? 1 : 0.6 }}>
                            <div>
                                <div style={{ color: 'var(--text-main)' }}>{t.name} <span style={muted}>· {KIND_LABELS[t.kind]} · {t.tasks.length} tasks{!t.isActive ? ' · not in use' : ''}</span></div>
                                {(t.applyWhen?.employmentTypes?.length > 0 || t.applyWhen?.departmentIds?.length > 0) && (
                                    <div style={muted}>Suggested for {[...(t.applyWhen.employmentTypes || []).map(x => TYPE_LABELS[x]), ...(t.applyWhen.departmentIds || []).map(id => departments.find(d => d.id === id)?.name).filter(Boolean)].join(', ')}</div>
                                )}
                            </div>
                            <button style={ghost} aria-label={`Edit ${t.name}`} onClick={() => setEditing(t)}>Edit</button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default ChecklistsTab;
